import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileSignature, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

interface WelcomeBannerProps {
  clientName?: string;
  onDismiss?: () => void;
}

export function WelcomeBanner({ clientName, onDismiss }: WelcomeBannerProps) {
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20, height: 0, marginBottom: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

            <CardContent className="relative p-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="absolute top-4 right-4 h-8 w-8 p-0 hover:bg-primary/10"
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                </div>

                <div className="flex-1 pr-8">
                  <h2 className="text-xl font-semibold mb-2">
                    Welcome to Your Client Portal
                    {clientName && `, ${clientName}`}!
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    Your MSA and SOW have been received successfully. This is
                    your central hub for managing your engagement—track
                    progress, review deliverables, manage billing, and more.
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileSignature className="h-4 w-4 text-emerald-500" />
                      <span>Contracts signed</span>
                    </div>
                    <div className="h-4 w-px bg-border" />
                    <Button
                      variant="link"
                      className="h-auto p-0 text-primary"
                      onClick={() => navigate("/service-orders")}
                    >
                      View your service orders
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
